// @flow
import * as React from 'react';
import {
  defineMessages,
  injectIntl,
  FormattedMessage,
  type IntlShape,
} from 'react-intl';
import Link from 'brastrap/containers/link/LinkContainer';

const LINK_DISABLED_CLASS = 'is-disabled';
const LABEL_CLASS = 'c-pagination__label';

const messages = defineMessages({
  nextPage: { id: 'pagination.next-page', defaultMessage: 'Goto Next page' },
  previousPage: {
    id: 'pagination.previous-page',
    defaultMessage: 'Goto Previous page',
  },
});

type AProps = {
  'aria-label': string,
  className: string,
  href: Url,
  onClick?: (e: Event) => void,
};

type Props = {
  id: string,
  intl: IntlShape,
  onClick: any => void,
  page: number,
  title?: string,
  total: number,
  maxItems: number,
  href: string,
  isMobile: String,
};

type LinkOnClick = (number: number) => void;

/**
 * Generates the link for an individual paginated item
 *
 * @param {String} href to populate the anchor's href attribute
 * @param {Function} onClick an optional callback handler when the link is clicked.
 * @param {String} label the text displayed as the link
 * @param {String} ariaLabel the aria-label for the link
 * @param {Number} number the page number for this link
 * @param {String} hash the hash fragment for this link
 * @returns {Object} the JSX for the link
 */
function generateLink(
  href: string,
  onClick: ?LinkOnClick,
  label: number | React.Element<*>,
  ariaLabel: number | string,
  number: number
) {
  // Href for page 1 anchor tag should not have page query string, but simply link
  // to root path of collection, which is what '.' does
  const formattedHref =
    number > 1 ? href.replace('{page}', number.toString()) : '.';

  const aProps: AProps = {
    'aria-label': ariaLabel.toString(),
    className: LABEL_CLASS,
    href: formattedHref,
  };

  if (onClick) {
    aProps.onClick = e => {
      e.preventDefault();
      if (onClick) {
        // needed to make flow happy
        onClick(number);
      }
    };
  }

  return <Link {...aProps}>{label}</Link>;
}

/**
 * Calculate which items to show, optionally limiting them to a viewable
 * window over the total list of items.
 *
 * @param {Number} current the currently displayed page number
 * @param {Number} total the total number of pages
 * @param {Number} maxItems an optional maximum number of pages to show, resulting in a sliding window
 *                 view over the items.
 * @returns {Number[]}
 */
export function getDisplayedItems(
  current: number,
  total: number,
  maxItems: number
) {
  let paginationStart = 1;
  let paginationEnd = total;

  if (maxItems && total > maxItems) {
    const paginationMargin = Math.floor(maxItems / 2);

    paginationStart = current - paginationMargin;
    paginationEnd = current + paginationMargin;

    if (paginationStart < 1) {
      paginationStart = 1;
      paginationEnd = Math.min(total, maxItems);
    } else if (paginationEnd > total) {
      paginationStart = Math.max(total - maxItems + 1, 1);
      paginationEnd = total;
    }
  }

  return [paginationStart, paginationEnd];
}

const Pagination = (props: Props) => {
  let nextLink = (
    <FormattedMessage id="pagination.next" defaultMessage="Next" />
  );
  let nextLiClassName = 'c-pagination__item';
  let previousLink = (
    <FormattedMessage id="pagination.previous" defaultMessage="Previous" />
  );
  let previousLiClassName = 'c-pagination__item';

  const current = props.page;
  const displayNextLink = current !== props.total;
  const displayPreviousLink = current > 1;

  const [paginationStart, paginationEnd] = getDisplayedItems(
    current,
    props.total,
    props.maxItems
  );

  if (displayNextLink) {
    nextLink = generateLink(
      props.href,
      props.onClick,
      nextLink,
      props.intl.formatMessage(messages.nextPage),
      current + 1
    );
  } else {
    nextLink = <span className={LABEL_CLASS}>{nextLink}</span>;
    nextLiClassName += ` ${LINK_DISABLED_CLASS}`;
  }

  if (displayPreviousLink) {
    previousLink = generateLink(
      props.href,
      props.onClick,
      previousLink,
      props.intl.formatMessage(messages.previousPage),
      current - 1
    );
  } else {
    previousLink = <span className={LABEL_CLASS}>{previousLink}</span>;
    previousLiClassName += ` ${LINK_DISABLED_CLASS}`;
  }

  const items = [];

  for (let i = paginationStart; i <= paginationEnd; i++) {
    let className = 'c-pagination__item';
    const isCurrent = current === i;
    const label = i;

    if (isCurrent) {
      className += ' is-current';
    }

    const linkAriaLabel = `Goto Page ${i}`;
    const currentAriaLabel = `Current Page, Page ${i}`;

    items.push(
      <li key={i} className={className}>
        {isCurrent ? (
          <strong
            aria-label={currentAriaLabel}
            aria-current="true"
            className={LABEL_CLASS}
          >
            {label}
          </strong>
        ) : (
          generateLink(props.href, props.onClick, label, linkAriaLabel, i)
        )}
      </li>
    );
  }

  if (props.total === 1) {
    return null;
  }

  const mobilePaginationDescription = `Page ${props.page} of ${props.total}`;
  const ariaLabelCurrentMobile = `Current Page, Page ${props.page}`;

  return (
    <nav className="c-pagination" role="navigation" aria-labelledby={props.id}>
      {props.title && (
        <h1 className="c-pagination__title u-hidden" id={props.id}>
          {props.title}
        </h1>
      )}
      {props.isMobile ? (
        <ul className="c-pagination-items__mobile">
          <li className={previousLiClassName}>{previousLink}</li>
          <ul className="c-mobile-pagination">
            <li>
              <p aria-label={ariaLabelCurrentMobile} className={LABEL_CLASS}>
                {mobilePaginationDescription}
              </p>
            </li>
          </ul>
          <li className={nextLiClassName}>{nextLink}</li>
        </ul>
      ) : (
        <ul className="c-pagination__items">
          <li className={previousLiClassName}>{previousLink}</li>
          {items}
          <li className={nextLiClassName}>{nextLink}</li>
        </ul>
      )}
    </nav>
  );
};

Pagination.defaultProps = {
  href: 'page={page}',
};

export default injectIntl(Pagination);
